import { useMemo } from "react";
import Chart from "@/components/Chart";
import EmptyState from "@/components/EmptyState";
import type { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import MetricGrid from "@/components/MetricGrid";
import { METRIC_CARD_MIN_WIDTH_PX } from "@/components/MetricGrid/metricGridLayout";
import SectionHeading from "@/components/SectionHeading";
import { extendedColors, sysColors } from "@/assets/theme/colors";
import type { Path } from "@/router";
import type { RosterActivityCohort, RosterActivityEvent } from "@/features/roster-activity";
import { summarizePeriod } from "../summarizePeriod";

type Props = {
  events: RosterActivityEvent[];
};

/** Matches EventGroups Tag types (standard): Member=tertiary, Associate=information, Student=secondary. */
const COHORT_CHART_COLORS: Record<RosterActivityCohort, string> = {
  MEMBER: sysColors["sys-brand-tertiary-fixed-dim"],
  ASSOCIATE: extendedColors["extended-pganavy-brand-color-container-bright"],
  STUDENT: sysColors["sys-brand-secondary-container"]
};

function EmptyChartState() {
  return <EmptyState title="No Roster Activity" caption="Try adjusting the filters." />;
}

export default function PeriodStats({ events }: Props) {
  const summary = useMemo(() => summarizePeriod(events), [events]);

  const metrics: MetricCardType<Path>[] = [
    {
      label: "Changes",
      value: String(summary.byGroup.changes),
      iconName: "sync_alt",
      testId: "roster-stat-changes"
    },
    {
      label: "Retirements",
      value: String(summary.byGroup.retirements),
      iconName: "beach_access",
      testId: "roster-stat-retirements"
    },
    {
      label: "In Memoriam",
      value: String(summary.byGroup.inMemoriam),
      iconName: "favorite",
      testId: "roster-stat-in-memoriam"
    }
  ];

  const cohortData = summary.byCohort.map(({ name, value }) => ({ name, value }));
  const cohortColors = summary.byCohort.map(({ cohort }) => COHORT_CHART_COLORS[cohort]);
  const cohortSummaryText =
    summary.byCohort.length === 0 ? null : summary.byCohort.map((entry) => `${entry.name}: ${entry.value}`).join(", ");
  const kindSummaryText =
    summary.byKind.length === 0 ? null : summary.byKind.map((entry) => `${entry.name}: ${entry.value}`).join(", ");

  return (
    <section data-testid="roster-activity-period-stats">
      <SectionHeading heading="Summary" body="This roll-up summary reflects the filters above." />
      <div className="flex flex-col gap-3">
        <MetricGrid metrics={metrics} minCardWidth={METRIC_CARD_MIN_WIDTH_PX} size="compact" hasHeading />

        <div className="grid items-stretch gap-4 md:grid-cols-2">
          <div>
            {kindSummaryText ? <p className="sr-only">{kindSummaryText}</p> : null}
            <Chart
              variant="bar"
              layout="vertical"
              title="Totals By Change Type"
              data={summary.byKind}
              bars={[{ dataKey: "value", name: "Events", color: sysColors["sys-brand-primary"] }]}
              showLegend={false}
              showValueLabels
              emptyLabel={<EmptyChartState />}
              barSize={24}
            />
          </div>
          <div>
            {cohortSummaryText ? <p className="sr-only">{cohortSummaryText}</p> : null}
            <Chart
              variant="pie"
              title="Totals By Member Type"
              data={cohortData}
              colors={cohortColors}
              formatAsCurrency={false}
              showTotal={false}
              emptyLabel={<EmptyChartState />}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
